import {setup} from "vue-class-component";
import {useApolloClient, useMutation, useQuery, useSubscription} from "@vue/apollo-composable";
import {DocumentNode, print} from "graphql";
import gql from "graphql-tag";
import dayjs from "dayjs";
import {random} from "lodash";

const mutationDocuments = new WeakMap<object, DocumentNode>()

/**
 * Run a graphql query using apollo-composable and return a context object which can be used in the components DOM
 * @param query the graphql query object
 * @param filter
 */
export function gqlQuery(query: DocumentNode, filter: any = null) {
    return setup(() => useQuery(query, filter))
}

export function gqlSubscription(query: DocumentNode, filter: any = null) {
    return setup(() => useSubscription(query, filter))
}

export function gqlMutation(query: DocumentNode, values: any = null) {
    const mutation = setup(() => useMutation(query, {variables: values}))
    mutationDocuments.set(mutation, query)
    return mutation
}

export function getGqlMutationDocument(mutation: object) {
    return mutationDocuments.get(mutation)
}

export function gqlQueryRaw(query: DocumentNode, values: any = null) {
    return useApolloClient().client.query({
        query: query,
        variables: values
    })
}

export function gqlQueryRawNetworkOnly(query: DocumentNode, values: any = null) {
    return useApolloClient().client.query({
        query,
        variables: values,
        fetchPolicy: "network-only"
    })
}

export function gqlQueryRawWithRole(
    query: DocumentNode,
    role: string,
    values: any = null,
    options: { fetchPolicy?: "cache-first" | "cache-only" | "network-only" | "no-cache" | "standby" } = {}
) {
    return useApolloClient().client.query({
        query,
        variables: values,
        fetchPolicy: options.fetchPolicy,
        context: {
            headers: {
                'x-hasura-role': role
            }
        }
    })
}

export function gqlMutationRaw(query: DocumentNode, variables: any = null) {
    const client = useApolloClient().client
    const isCombinedMutation = !query.loc?.source?.body
    const transactionKey = random(true) + '-' + dayjs().millisecond()
    if (isCombinedMutation) insertTransaction(print(query), variables, transactionKey)

    return client.mutate({mutation: query, variables})
        .then(response => {
            if (isCombinedMutation) insertTransactionResponse(response, transactionKey)
            return response
        }).catch(error => {
            console.error('[GraphQL Mutation Error]', error)
            throw error
        })
}

function insertTransaction(mutationString: string, variables: any, transactionKey: string) {

    try {
        const object = {
            mutation: mutationString,
            variables,
            transaction_key: transactionKey
        }

        useApolloClient().client.mutate({
            mutation: gql`
                mutation addTransaction(
                    $object: transaction_insert_input!
                ) {
                    insert_transaction_one(object: $object) {
                        id
                    }
                }
            `,
            variables: {object}
        }).then(response => {
            console.info('Transaction inserted: ' + transactionKey)
        })

    } catch (e) {
        console.error('Error inserting transaction.', {
            mutationString,
            variables,
            transactionKey
        }, e)
    }

}

function insertTransactionResponse(response: any, transactionKey: string) {

    try {
        const variables = {response, transaction_key: transactionKey}
        useApolloClient().client.mutate({
            mutation: gql`
                mutation addTransactionResponse(
                    $transaction_key: String,
                    $response: jsonb
                ) {
                    update_transaction(
                        where: {transaction_key: {_eq: $transaction_key}},
                        _set: {response: $response}
                    ) {
                        returning {
                            id
                        }
                    }
                }
            `,
            variables
        }).then(response => {
            console.error('Transaction saved: ' + transactionKey)
        })

    } catch (e) {
        console.error('Error saving transaction response.', {
            response,
            transactionKey
        }, e)
    }

}
